import type { KeyedMarketDataSnapshot, UseMultiMarketDataSnapshotOutput } from '@talos/kyoko';
import type { Column } from 'ag-grid-community';
import { filter, map, merge, scan, share, type Observable } from 'rxjs';
import { getPriceGridColumnTypeFromColDef } from '../../columns';
import type { PriceGridEntity } from '../../PriceGridEntity';
import type { PriceGridMarketDataEnrichmentOutput, PriceGridMarketDataEnrichmentSpec } from './types';

interface PriceGridMarketDataEnrichmentState {
  cachesBySpecID: Map<string, Map<string, KeyedMarketDataSnapshot>>;
  output?: UseMultiMarketDataSnapshotOutput;
}

/** Matches the column types that carry market data params on their colDef, used by the shared Price Grid columns. */
export function hasPriceGridColumnType({ column, columnTypes }: { column: Column; columnTypes: string[] }) {
  const talosColumnType = getPriceGridColumnTypeFromColDef(column.getColDef());
  return talosColumnType != null && columnTypes.includes(talosColumnType);
}

export function isPriceGridMarketDataColumnRelevant({
  column,
  specs,
}: {
  column: Column;
  specs: PriceGridMarketDataEnrichmentSpec<unknown>[];
}) {
  return specs.some(spec => spec.isColumnRelevant(column));
}

export function getPriceGridMarketDataEnrichmentLookupKey({
  specs,
  item,
  column,
}: {
  specs: PriceGridMarketDataEnrichmentSpec<unknown>[];
  item: PriceGridEntity;
  column: Column;
}) {
  for (const spec of specs) {
    const lookupKey = spec.getLookupKey({ item, column });
    if (lookupKey) {
      return lookupKey;
    }
  }

  return undefined;
}

export function getPriceGridEnrichmentRequestsObs<TRequest>({
  spec,
  visibleCellsChangedObs,
}: {
  spec: PriceGridMarketDataEnrichmentSpec<TRequest>;
  visibleCellsChangedObs: Observable<{ visibleColumns: Column[]; renderedItems: PriceGridEntity[] }>;
}): Observable<TRequest[]> {
  return visibleCellsChangedObs.pipe(
    map(({ visibleColumns, renderedItems }) =>
      spec.buildRequests({
        visibleColumns,
        renderedItems,
      })
    )
  );
}

export function combinePriceGridMarketDataEnrichmentOutputs(
  enrichmentOutputs: PriceGridMarketDataEnrichmentOutput[]
): Observable<UseMultiMarketDataSnapshotOutput> {
  return merge(
    ...enrichmentOutputs.map(enrichmentOutput =>
      enrichmentOutput.observable.pipe(map(output => ({ specID: enrichmentOutput.specID, output })))
    )
  ).pipe(
    scan(
      (state: PriceGridMarketDataEnrichmentState, { specID, output }): PriceGridMarketDataEnrichmentState => {
        const cachesBySpecID = new Map(state.cachesBySpecID);
        cachesBySpecID.set(specID, output.cache);

        return {
          cachesBySpecID,
          output: {
            json: output.json,
            cache: new Map([...cachesBySpecID.values()].flatMap(cache => [...cache])),
          },
        };
      },
      {
        cachesBySpecID: new Map<string, Map<string, KeyedMarketDataSnapshot>>(),
      }
    ),
    map(({ output }) => output),
    filter((output): output is UseMultiMarketDataSnapshotOutput => output != null),
    share()
  );
}
